import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Alert,
    LinearProgress,
    TextField,
    InputAdornment,
    Button,
    Tooltip,
    Avatar,
    Select,
    MenuItem,
    FormControl,
    Stack,
    Paper,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Timeline as TimelineIcon,
    Search as SearchIcon,
    PictureAsPdf as PdfIcon,
    InfoOutlined as InfoIcon,
    GroupsOutlined as GroupsIcon,
    BoltOutlined as BoltIcon,
    ScheduleOutlined as ScheduleIcon,
    PaidOutlined as PaidIcon,
    SwapVert as SortIcon,
} from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import UserActivityService from '../../Services/userActivity.service';

const ACTIVITY_KEYS = [
    'Upload',
    'Download',
    'ViewData',
    'GenerateDocument',
    'ImportData',
    'ExportData',
    'CreateEntities',
    'DeleteData',
    'EditData',
];

const ACTIVITY_COLORS = {
    Upload: '#3b82f6',
    Download: '#8b5cf6',
    ViewData: '#06b6d4',
    GenerateDocument: '#f59e0b',
    ImportData: '#10b981',
    ExportData: '#14b8a6',
    CreateEntities: '#6366f1',
    DeleteData: '#ef4444',
    EditData: '#f97316',
};

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const formatHours = (hours) => {
    if (!hours || hours === 0) return '0:00';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}`;
};

const formatCurrency = (amount) =>
    `₪${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const normalizeCounts = (countsByType) => {
    if (!countsByType) return {};
    const result = {};
    Object.entries(countsByType).forEach(([key, val]) => {
        let name = key;
        if (/^\d+$/.test(String(key))) {
            const enumVal = parseInt(key, 10);
            name = ACTIVITY_KEYS[enumVal - 1] || key;
        }
        if (ACTIVITY_KEYS.includes(name)) {
            result[name] = (result[name] || 0) + val;
        }
    });
    return result;
};

const initials = (name = '') =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0])
        .join('')
        .toUpperCase() || '?';

/* ---- Small reusable pieces ------------------------------------------------ */

function StatCard({ icon, label, value, color }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: '100%',
                transition: 'box-shadow .2s, transform .2s',
                '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.06)', transform: 'translateY(-1px)' },
            }}
        >
            <Box
                sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: `${color}18`,
                    color,
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }} noWrap>
                    {value}
                </Typography>
            </Box>
        </Paper>
    );
}

/** Thin horizontal stacked bar showing the distribution across activity types. */
function DistributionBar({ counts, total, height = 8 }) {
    const { t } = useTranslation();
    if (!total) {
        return <Box sx={{ height, borderRadius: 5, bgcolor: 'action.hover' }} />;
    }
    return (
        <Box sx={{ display: 'flex', height, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.hover' }}>
            {ACTIVITY_KEYS.map((k) => {
                const c = counts[k] || 0;
                if (!c) return null;
                const pct = (c / total) * 100;
                return (
                    <Tooltip key={k} title={`${t(k)}: ${c} (${Math.round(pct)}%)`} arrow>
                        <Box sx={{ width: `${pct}%`, bgcolor: ACTIVITY_COLORS[k], transition: 'filter .15s', '&:hover': { filter: 'brightness(1.1)' } }} />
                    </Tooltip>
                );
            })}
        </Box>
    );
}

/* ---- Main component ------------------------------------------------------- */

export default function ClientActivityReportView({ accountantId, filters, token, direction }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [report, setReport] = useState(null);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('activities');
    const [expandedCase, setExpandedCase] = useState(false);

    const buildPayload = () => ({
        AccountantId: accountantId,
        StartUtc: filters.startUtc.toISOString(),
        EndUtc: filters.endUtc.toISOString(),
        CaseIds: filters.clientCaseIds?.map((c) => c.value).filter(Boolean) || [],
        SubAccountantIds: filters.subAccountantIds?.map((sa) => sa.value) || [],
        ActivityType: filters.activityType || null,
    });

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        accountantId,
        filters.startUtc,
        filters.endUtc,
        filters.subAccountantIds,
        filters.clientCaseIds,
        filters.activityType,
        token,
    ]);

    const loadReport = async () => {
        setLoading(true);
        try {
            const response = await UserActivityService.GetClientActivityReport(buildPayload(), token);
            if (response.ok) {
                const data = await response.json();
                setReport(data);
                if (data.cases?.length === 1) {
                    setExpandedCase(data.cases[0].caseId ?? 'office');
                }
            } else {
                setReport(null);
            }
        } catch (err) {
            console.error('Failed to load client activity report', err);
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const response = await UserActivityService.GetClientActivityReportPdf(buildPayload(), token);
            if (!response.ok) throw new Error('PDF generation failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ClientActivityReport_${dayjs(filters.startUtc).format('YYYYMM')}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('PDF download failed', err);
        } finally {
            setDownloading(false);
        }
    };

    const maxActivities = useMemo(
        () => (report?.cases?.length ? Math.max(...report.cases.map((c) => c.totalActivities || 0)) : 0),
        [report]
    );

    const visibleCases = useMemo(() => {
        if (!report?.cases) return [];
        const q = search.trim().toLowerCase();
        let list = q
            ? report.cases.filter(
                  (c) =>
                      (c.caseName || '').toLowerCase().includes(q) ||
                      (c.caseId || '').toLowerCase().includes(q)
              )
            : [...report.cases];

        const sorters = {
            activities: (a, b) => (b.totalActivities || 0) - (a.totalActivities || 0),
            cost: (a, b) => (b.totalCostFromMouse || 0) - (a.totalCostFromMouse || 0),
            hours: (a, b) => (b.totalMouseHours || 0) - (a.totalMouseHours || 0),
            name: (a, b) => (a.caseName || '').localeCompare(b.caseName || ''),
        };
        return list.sort(sorters[sortBy] || sorters.activities);
    }, [report, search, sortBy]);

    const periodLabel = useMemo(() => {
        if (!filters?.startUtc || !filters?.endUtc) return '';
        return `${dayjs(filters.startUtc).format('MM/YYYY')} – ${dayjs(filters.endUtc).format('MM/YYYY')}`;
    }, [filters]);

    /* ---- global activity mix (for the legend/insight strip) ---- */
    const globalMix = useMemo(() => {
        const totals = {};
        (report?.cases || []).forEach((c) => {
            const counts = normalizeCounts(c.countsByType);
            ACTIVITY_KEYS.forEach((k) => {
                totals[k] = (totals[k] || 0) + (counts[k] || 0);
            });
        });
        return totals;
    }, [report]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    {t('Loading') || 'Loading...'}
                </Typography>
            </Box>
        );
    }

    if (!report?.cases?.length) {
        return (
            <Box sx={{ px: 2, mt: 2 }}>
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    {t('NoClientActivityData') || 'No client activity data for the selected period and filters.'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box dir={direction} sx={{ px: { xs: 1, md: 2 }, mt: 1.5, pb: 4 }}>
            {/* ── Toolbar ─────────────────────────────────────────────── */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    background: 'linear-gradient(135deg, #fafbff 0%, #ffffff 100%)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2,
                            background: GRADIENT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                        }}
                    >
                        <BusinessIcon fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                            {t('ClientActivityDetailedReport') || 'Detailed Client Activity Report'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {periodLabel}
                        </Typography>
                    </Box>
                    <Tooltip
                        arrow
                        title={
                            t('ClientActivityReportDescription') ||
                            'For each client (case): activity types performed, which employees performed them, daily trends, work hours and estimated cost.'
                        }
                    >
                        <InfoIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
                    </Tooltip>
                </Box>

                <TextField
                    size="small"
                    placeholder={t('SearchClient') || 'Search client...'}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        startAdornment={
                            <InputAdornment position="start">
                                <SortIcon fontSize="small" />
                            </InputAdornment>
                        }
                        sx={{ borderRadius: 2, bgcolor: '#fff' }}
                    >
                        <MenuItem value="activities">{t('SortByActivities') || 'Most activities'}</MenuItem>
                        <MenuItem value="cost">{t('SortByCost') || 'Highest cost'}</MenuItem>
                        <MenuItem value="hours">{t('SortByHours') || 'Most hours'}</MenuItem>
                        <MenuItem value="name">{t('SortByName') || 'Name'}</MenuItem>
                    </Select>
                </FormControl>

                <Button
                    variant="contained"
                    size="small"
                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <PdfIcon />}
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: GRADIENT, boxShadow: 'none' }}
                >
                    {t('DownloadDetailedReport') || 'Download PDF'}
                </Button>
            </Paper>

            {/* ── KPI cards ───────────────────────────────────────────── */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}>
                    <StatCard
                        icon={<GroupsIcon />}
                        color="#667eea"
                        label={t('ClientsWithActivity') || 'Clients with activity'}
                        value={report.cases.length}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard
                        icon={<BoltIcon />}
                        color="#2980b9"
                        label={t('TotalActivities') || 'Total activities'}
                        value={(report.grandTotalActivities || 0).toLocaleString()}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard
                        icon={<ScheduleIcon />}
                        color="#16a085"
                        label={t('MouseHours') || 'Work hours'}
                        value={`${formatHours(report.grandTotalMouseHours)}h`}
                    />
                </Grid>
                <Grid item xs={6} md={3}>
                    <StatCard
                        icon={<PaidIcon />}
                        color="#e74c3c"
                        label={t('TotalCost') || 'Total cost'}
                        value={formatCurrency(report.grandTotalCostFromMouse)}
                    />
                </Grid>
            </Grid>

            {/* ── Global activity-type legend + mix ───────────────────── */}
            <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.75 }}>
                    {t('ActivityTypeBreakdown') || 'Activity mix'}
                </Typography>
                <DistributionBar counts={globalMix} total={report.grandTotalActivities} height={10} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
                    {ACTIVITY_KEYS.filter((k) => globalMix[k] > 0).map((k) => (
                        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: ACTIVITY_COLORS[k] }} />
                            <Typography variant="caption" color="text.secondary">
                                {t(k)} · <b>{globalMix[k].toLocaleString()}</b>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Paper>

            {/* ── Client rows ─────────────────────────────────────────── */}
            <Stack spacing={1}>
                {visibleCases.map((caseItem, idx) => {
                    const caseKey = caseItem.caseId ?? 'office';
                    const counts = normalizeCounts(caseItem.countsByType);
                    const sharePct = maxActivities > 0 ? (caseItem.totalActivities / maxActivities) * 100 : 0;
                    const isOpen = expandedCase === caseKey;

                    const dailyDataset = (caseItem.dailyBreakdown || []).map((d) => ({
                        day: dayjs(d.date).format('DD/MM'),
                        total: d.total,
                    }));

                    return (
                        <Accordion
                            key={caseKey}
                            expanded={isOpen}
                            onChange={(_, exp) => setExpandedCase(exp ? caseKey : false)}
                            elevation={0}
                            disableGutters
                            sx={{
                                borderRadius: '14px !important',
                                border: '1px solid',
                                borderColor: isOpen ? 'primary.light' : 'divider',
                                overflow: 'hidden',
                                transition: 'border-color .2s, box-shadow .2s',
                                boxShadow: isOpen ? '0 6px 22px rgba(102,126,234,0.12)' : 'none',
                                '&:before': { display: 'none' },
                                '&:hover': { borderColor: 'primary.light' },
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ px: 1.5, '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
                                    {/* rank + avatar */}
                                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                        <Avatar
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                fontSize: 14,
                                                fontWeight: 700,
                                                bgcolor: caseItem.caseId ? 'primary.main' : 'grey.500',
                                            }}
                                        >
                                            {caseItem.caseId ? initials(caseItem.caseName) : <BusinessIcon fontSize="small" />}
                                        </Avatar>
                                        {idx < 3 && sortBy === 'activities' && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -4,
                                                    insetInlineEnd: -4,
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    bgcolor: ['#f5c518', '#c0c0c0', '#cd7f32'][idx],
                                                    color: '#fff',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px solid #fff',
                                                }}
                                            >
                                                {idx + 1}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* name + distribution */}
                                    <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                                        <Typography fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>
                                            {caseItem.caseName}
                                        </Typography>
                                        <Box sx={{ mt: 0.5, maxWidth: 320 }}>
                                            <DistributionBar counts={counts} total={caseItem.totalActivities} />
                                        </Box>
                                    </Box>

                                    {/* metrics */}
                                    <Stack
                                        direction="row"
                                        spacing={2.5}
                                        sx={{ ml: 'auto', flexShrink: 0, display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}
                                    >
                                        <Metric value={caseItem.totalActivities?.toLocaleString()} label={t('Activities')} color="#2980b9" />
                                        <Metric value={`${formatHours(caseItem.totalMouseHours)}h`} label={t('MouseHours')} color="#16a085" />
                                        <Metric value={formatCurrency(caseItem.totalCostFromMouse)} label={t('TotalCost')} color="#e74c3c" />
                                    </Stack>

                                    {/* share bar */}
                                    <Box sx={{ width: 60, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={sharePct}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { background: GRADIENT } }}
                                        />
                                    </Box>
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 2, bgcolor: 'grey.50' }}>
                                {/* mobile metrics */}
                                <Stack direction="row" spacing={2} sx={{ display: { xs: 'flex', sm: 'none' }, mb: 1.5 }}>
                                    <Metric value={caseItem.totalActivities?.toLocaleString()} label={t('Activities')} color="#2980b9" />
                                    <Metric value={`${formatHours(caseItem.totalMouseHours)}h`} label={t('MouseHours')} color="#16a085" />
                                    <Metric value={formatCurrency(caseItem.totalCostFromMouse)} label={t('TotalCost')} color="#e74c3c" />
                                </Stack>

                                <Grid container spacing={1.5}>
                                    {/* Activity types */}
                                    <Grid item xs={12} md={5}>
                                        <SectionCard icon={<TimelineIcon fontSize="small" />} title={t('ActivityTypeBreakdown') || 'By activity type'}>
                                            <Stack spacing={0.75}>
                                                {ACTIVITY_KEYS.filter((k) => counts[k] > 0).map((k) => {
                                                    const c = counts[k];
                                                    const pct = caseItem.totalActivities > 0 ? (c / caseItem.totalActivities) * 100 : 0;
                                                    return (
                                                        <Box key={k}>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                                                                <Typography variant="caption" fontWeight={600}>
                                                                    {t(k)}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {c} · {Math.round(pct)}%
                                                                </Typography>
                                                            </Box>
                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={pct}
                                                                sx={{
                                                                    height: 5,
                                                                    borderRadius: 3,
                                                                    bgcolor: 'action.hover',
                                                                    '& .MuiLinearProgress-bar': { bgcolor: ACTIVITY_COLORS[k], borderRadius: 3 },
                                                                }}
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </SectionCard>
                                    </Grid>

                                    {/* Daily trend */}
                                    <Grid item xs={12} md={7}>
                                        <SectionCard icon={<TimelineIcon fontSize="small" />} title={t('DailyActivityTimeline') || 'Daily trend'}>
                                            {dailyDataset.length > 0 ? (
                                                <BarChart
                                                    dataset={dailyDataset}
                                                    xAxis={[{ scaleType: 'band', dataKey: 'day', tickLabelStyle: { fontSize: 10 } }]}
                                                    series={[{ dataKey: 'total', label: t('Activities'), color: '#667eea' }]}
                                                    height={150}
                                                    margin={{ top: 10, bottom: 24, left: 34, right: 8 }}
                                                    slotProps={{ legend: { hidden: true } }}
                                                />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('NoDataToDisplay') || 'No data'}
                                                </Typography>
                                            )}
                                        </SectionCard>
                                    </Grid>

                                    {/* Employees */}
                                    <Grid item xs={12}>
                                        <SectionCard icon={<PersonIcon fontSize="small" />} title={t('ActivityByEmployee') || 'By employee'}>
                                            <TableContainer sx={{ maxHeight: 260 }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>{t('Employee')}</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>{t('Activities')}</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>{t('MouseHours')}</TableCell>
                                                            <TableCell align="center" sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>{t('CostFromMouse')}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700, bgcolor: 'grey.100', minWidth: 140 }}>{t('ActivityTypeBreakdown')}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(caseItem.users || []).map((user) => {
                                                            const userCounts = normalizeCounts(user.countsByType);
                                                            return (
                                                                <TableRow key={user.userId} hover>
                                                                    <TableCell>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: 'secondary.main' }}>
                                                                                {initials(user.name)}
                                                                            </Avatar>
                                                                            <Typography variant="body2" fontWeight={600} noWrap>
                                                                                {user.name}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell align="center">
                                                                        <Chip label={user.totalActivities} size="small" color="primary" variant="outlined" sx={{ height: 22 }} />
                                                                    </TableCell>
                                                                    <TableCell align="center">{formatHours(user.mouseHours)}h</TableCell>
                                                                    <TableCell align="center" sx={{ color: '#e74c3c', fontWeight: 600 }}>
                                                                        {formatCurrency(user.costFromMouse)}
                                                                    </TableCell>
                                                                    <TableCell sx={{ minWidth: 140 }}>
                                                                        <DistributionBar counts={userCounts} total={user.totalActivities} height={7} />
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </SectionCard>
                                    </Grid>
                                </Grid>

                                {caseItem.efficiencyFromMouse > 0 && (
                                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Chip
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            label={`${t('CaseEfficiencySummary') || 'Efficiency'}: ${formatCurrency(
                                                caseItem.efficiencyFromMouse
                                            )} / ${t('Activity') || 'activity'}`}
                                        />
                                        {caseItem.lastActivityDate && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('LastActivity') || 'Last activity'}: {dayjs(caseItem.lastActivityDate).format('DD/MM/YYYY')}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </Box>
    );
}

/* ---- tiny inline helpers -------------------------------------------------- */

function Metric({ value, label, color }) {
    return (
        <Box sx={{ textAlign: 'center', minWidth: 56 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ color, lineHeight: 1.1 }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 10 }}>
                {label}
            </Typography>
        </Box>
    );
}

function SectionCard({ icon, title, children }) {
    return (
        <Card variant="outlined" sx={{ borderRadius: 2.5, height: '100%' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, textTransform: 'uppercase', letterSpacing: 0.4 }}
                >
                    {icon}
                    {title}
                </Typography>
                {children}
            </CardContent>
        </Card>
    );
}
